import { useEffect, useRef } from "react";
import { i18n } from "../i18n";

/*
 * Cloudflare Turnstile on the signup form: the browser proves somebody is at
 * it, without asking them anything.
 *
 * The API's half is `middleware/turnstile.ts`, keyed on its secret; this half
 * is keyed on the public site key, and the two are one widget. Unset -- local
 * development, a deployment that has not set it up -- nothing renders, nothing
 * loads and the form submits as it always did. Set, the widget mounts, hands
 * the form a token when it has one, and the API refuses the signup without it.
 *
 * Loaded from Cloudflare rather than bundled, which is the only way it can be:
 * the challenge is the script. The CSP in `scripts/headers.mjs` allows that
 * origin exactly when the site key is set, so the two cannot disagree.
 *
 * Explicit rendering rather than the `cf-turnstile` class, because React owns
 * this DOM and the implicit mode scans the document once at script load --
 * before a client-routed page has mounted.
 */

export const TURNSTILE_SITE_KEY: string | null =
  import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() || null;

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
      theme: "light" | "dark" | "auto";
      language: string;
      size: "normal" | "flexible" | "compact";
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** The script, fetched once however many times a form mounts. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile did not load")));
    script.onerror = () => {
      loading = null;
      reject(new Error("turnstile did not load"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

interface TurnstileProps {
  /** A token when the challenge passes; `null` when it expires or fails. */
  onToken: (token: string | null) => void;
  /**
   * Bump to ask for a fresh token. A token is spent by the one request that
   * carries it, so a signup the API refused for another reason -- an address
   * already taken -- needs a new one before the form can be sent again.
   */
  generation?: number;
}

export function Turnstile({ onToken, generation = 0 }: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const latest = useRef(onToken);
  latest.current = onToken;

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !container.current) return;
    let cancelled = false;
    void loadTurnstile()
      .then((api) => {
        if (cancelled || !container.current) return;
        widget.current = api.render(container.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => latest.current(token),
          "expired-callback": () => latest.current(null),
          "error-callback": () => latest.current(null),
          theme: "dark",
          language: i18n.language,
          size: "flexible",
        });
      })
      .catch(() => latest.current(null));
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, []);

  useEffect(() => {
    if (generation > 0 && widget.current && window.turnstile) {
      latest.current(null);
      window.turnstile.reset(widget.current);
    }
  }, [generation]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={container} data-turnstile="" className="min-h-[65px]" />;
}
